import React, { useCallback, useEffect, useRef, useState } from 'react';
import Notiflix from 'notiflix';
import { DataTableSelectionChangeParams } from 'primereact/datatable';
import { FormHandles } from '@unform/core';
import { useHistory } from 'react-router-dom';
import Modal2 from '../../components/Modal2';
import { Button } from '../Tutorials/styles';
import Input from '../../components/Input';
import { HolderContentStyled, ModalFormWrapper, Table } from './modal-styles';
import { Column } from '../../components/Table';
import api from '../../services/api/api';
import AddField from '../../utils/tableUtils';
import possibleHolderStatusTag from '../../utils/possibleHolderStatusTag';
import Select, { OptionProps, SelectHandles } from '../../components/Select';

const convertTitularToDependenteUrl = (requestId: string | number): string => {
  return `/incluir-beneficiario/${requestId}/edicao/dependente`;
};

export interface ToDependentModalProps {
  beneficiaryName: string;
  contractId: string;
  requestId: string;
}

export interface BeneficiaryModalTable {
  holderId: number;
  cardId: number;
  status: string;
  beneficiaryName: string;
  statusId: number;
}

interface Status {
  statusId: string;
  status: string;
}

interface SearchBeneficiaries {
  statusId: string;
  cardId: string;
  requestId: string;
}

const searchFormInitialValues: SearchBeneficiaries = {
  statusId: '',
  cardId: '',
  requestId: '',
};

interface HolderModalContentProps {
  beneficiaryName: string;
  contractId: string;
  requestId: string;
  handleConfirm: (callback: () => Promise<boolean>) => void;
  getDataToTable: (filter?: any) => void;
}

export const ConfirmModalContent = ({
  callback,
  handleOpenSuccessModal,
}: {
  callback: () => Promise<boolean>;
  handleOpenSuccessModal: () => void;
}) => {
  return (
    <div
      style={{
        display: 'flex',
        gap: '1rem',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Button
        modal
        onClick={async () => {
          try {
            const response = await callback();

            if (response) {
              Modal2.Close();
              handleOpenSuccessModal();
            }
          } catch (error) {
            Notiflix.Notify.failure(
              'Ocorreu um erro ao realizar a troca de titular.',
              {
                zindex: 999999999999,
              },
            );
          }
        }}
        style={{ backgroundColor: '#0a5f55', color: '#fff' }}
      >
        Confirmar
      </Button>

      <Button
        secondary
        modal
        onClick={() => Modal2.Close()}
        style={{
          backgroundColor: 'transparent',
          border: '1px solid #676767',
        }}
      >
        Cancelar
      </Button>
    </div>
  );
};

const HolderModalContent = ({
  beneficiaryName,
  contractId,
  requestId,
  handleConfirm,
  getDataToTable,
}: HolderModalContentProps) => {
  const formRef = useRef<FormHandles>(null);
  const selectHandlerRef = useRef<SelectHandles>(null);
  const [beneficiaryTableData, setBeneficiaryTableData] = useState<
    Partial<BeneficiaryModalTable>[]
  >([]);
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [selectedBeneficiary, setSelectedBeneficiary] =
    useState<BeneficiaryModalTable | null>(null);
  const [statusOptions, setStatusOptions] = useState<OptionProps[]>([
    { title: 'Selecione...', value: '' },
  ]);
  const [statusSelected, setStatusSelected] = useState<string | undefined>();
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchProbableOwner = useCallback(
    (search?: Partial<SearchBeneficiaries>, page = 1, isNewSearch = false) => {
      if (isNewSearch || page === 1) {
        setIsTableLoading(true);
        Notiflix.Block.circle('.notiflix-table-loading');
      } else {
        setIsLoadingMore(true);
      }

      api
        .get('/beneficiary-dependent-holder/list-probable-holder', {
          params: {
            contractId,
            statusId: search?.statusId,
            cardId: search?.cardId,
            requestId: Number(search?.requestId) || undefined,
            holderName: Number.isNaN(Number(search?.requestId))
              ? search?.requestId
              : undefined,
            page,
          },
        })
        .then((res) => {
          const newData = res.data?.data || [];

          if (isNewSearch || page === 1) {
            setBeneficiaryTableData(newData);
            setCurrentPage(1);
          } else {
            setBeneficiaryTableData((prev) => [...prev, ...newData]);
            setCurrentPage(page);
          }

          setHasMoreData(newData.length === 10);
        })
        .catch((error) => {
          console.error('Erro ao carregar dados:', error);
          Notiflix.Notify.failure('Erro ao carregar dados');
        })
        .finally(() => {
          if (isNewSearch || page === 1) {
            setIsTableLoading(false);
            Notiflix.Block.remove('.notiflix-table-loading');
          } else {
            setIsLoadingMore(false);
          }
        });
    },
    [contractId],
  );

  const fetchStatusSelectOptions = useCallback(() => {
    Notiflix.Block.circle('.notiflix-select-loading');

    api
      .get('/beneficiary-dependent-holder/list-status', {
        params: {
          contractId,
        },
      })
      .then((res) => {
        if (Array.isArray(res.data)) {
          return;
        }
        const options =
          res.data?.data?.status?.map(
            (s: Status) =>
              ({
                value: s.statusId,
                title: s.status,
              } as OptionProps),
          ) ?? [];
        setStatusOptions([{ title: 'Selecione...', value: '' }, ...options]);
      })
      .finally(() => {
        Notiflix.Block.remove('.notiflix-select-loading');
      });
  }, [contractId]);

  const handleSelectedBeneficiary = useCallback(
    (e: DataTableSelectionChangeParams) => setSelectedBeneficiary(e.value),
    [],
  );

  const handleSelectHolder = () => {
    if (!selectedBeneficiary) {
      Notiflix.Notify.failure(
        'Por favor, selecione um beneficiário para continuar.',
        {
          zindex: 999999999999,
        },
      );
      return;
    }

    handleConfirm(async () => {
      try {
        Notiflix.Block.circle('.notiflix-confirm-button');

        await api.post('/beneficiary-dependent-holder/to-dependent', {
          requestId,
          cardId: selectedBeneficiary?.cardId,
          holderId: selectedBeneficiary?.holderId,
        });

        Notiflix.Notify.success('Conversão realizado com sucesso!');
        getDataToTable();

        // Navigate to dependente URL after successful conversion
        const newUrl = convertTitularToDependenteUrl(requestId);
        console.log(newUrl);
        window.location.href = newUrl;

        return true;
      } catch (err: any) {
        Notiflix.Notify.failure(
          err.response?.data?.message ??
            'Falha ao converter titular a beneficiário',
        );
        return false;
      } finally {
        Notiflix.Block.remove('.notiflix-confirm-button');
      }
    });
  };

  const handleSearchButtonClick = useCallback(
    (formData: Partial<SearchBeneficiaries>) => {
      setCurrentPage(1);
      setHasMoreData(true);
      setSelectedBeneficiary(null);

      fetchProbableOwner(
        {
          statusId: statusSelected || '',
          cardId: formData.cardId || '',
          requestId: formData.requestId || '',
        },
        1,
        true,
      );
    },
    [fetchProbableOwner, statusSelected],
  );

  const handleStatusChange = useCallback(
    (value: string) => setStatusSelected(value),
    [],
  );

  const handleCleanSearchForm = useCallback(() => {
    selectHandlerRef.current?.setValue('Selecione...', '');
    formRef.current?.reset(searchFormInitialValues);
    setSelectedBeneficiary(null);
    setCurrentPage(1);
    setHasMoreData(true);
    setStatusSelected('');
    fetchProbableOwner(
      {
        statusId: '',
        cardId: '',
        requestId: '',
      },
      1,
      true,
    );
  }, [selectHandlerRef, formRef, fetchProbableOwner]);

  const handleLoadMore = useCallback(
    (rowsToShow: number) => {
      if (isLoadingMore || !hasMoreData) return;

      const currentSearch =
        formRef.current?.getData() as Partial<SearchBeneficiaries>;

      fetchProbableOwner(
        { ...currentSearch, statusId: statusSelected },
        currentPage + 1,
        false,
      );
    },
    [
      fetchProbableOwner,
      statusSelected,
      currentPage,
      hasMoreData,
      isLoadingMore,
    ],
  );

  useEffect(() => {
    fetchProbableOwner({}, 1, true);
    fetchStatusSelectOptions();
  }, [fetchStatusSelectOptions]);

  return (
    <HolderContentStyled>
      <h3>Dependente: {beneficiaryName}</h3>

      <ModalFormWrapper
        onSubmit={handleSearchButtonClick}
        ref={formRef}
        className="form-change-holder"
      >
        <div className="form-group-content">
          <div className="form-group">
            <Select
              name="statusId"
              title="Status:"
              ref={selectHandlerRef}
              className="notiflix-select-loading"
              options={statusOptions}
              changeCallback={handleStatusChange}
            />

            <Input
              name="cardId"
              label="Carteirinha"
              placeholder="Número da carteirinha"
            />

            <Button
              type="submit"
              modal
              disabled={isTableLoading}
              style={{ backgroundColor: '#0a5f55', color: '#fff' }}
            >
              Consultar
            </Button>
          </div>

          <div className="form-group">
            <Input
              name="requestId"
              label="Beneficiário"
              placeholder="Digite o nome ou ID do beneficiário"
            />

            <Button
              secondary
              disabled={isTableLoading}
              onClick={handleCleanSearchForm}
            >
              Limpar
            </Button>
          </div>
        </div>

        <div className="responsive-buttons">
          <Button
            type="submit"
            modal
            disabled={isTableLoading}
            style={{ backgroundColor: '#0a5f55', color: '#fff' }}
          >
            Consultar
          </Button>
          <Button
            secondary
            disabled={isTableLoading}
            onClick={handleCleanSearchForm}
          >
            Limpar
          </Button>
        </div>

        <div className="table-container">
          <Table
            loading={isTableLoading}
            items={beneficiaryTableData}
            selection={selectedBeneficiary}
            onSelectionChange={handleSelectedBeneficiary}
            className="notiflix-table-loading"
            lazy
            hideFooter={!hasMoreData}
            disableRows
            callbackShowMore={handleLoadMore}
            maxItems={hasMoreData ? 999999 : beneficiaryTableData.length}
          >
            <Column selectionMode="single" headerStyle={{ width: '50px' }} />
            <Column
              field="holderId"
              header="Solicitação"
              sortable
              body={(colData) => AddField(colData.holderId, 'Solicitação')}
              style={{ width: '140px' }}
            />
            <Column
              field="cardId"
              header="Carteirinha"
              sortable
              body={(colData) => AddField(colData.cardId, 'Carteirinha')}
            />
            <Column
              field="beneficiaryName"
              header="Titular"
              sortable
              body={(colData) => AddField(colData.beneficiaryName, 'Titular')}
            />
            <Column
              field="status"
              header="Situação"
              headerStyle={{
                textAlign: 'center',
              }}
              sortable
              body={(colData) =>
                possibleHolderStatusTag(colData.statusId, colData.status)
              }
            />
          </Table>
        </div>
      </ModalFormWrapper>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '1rem',
          gap: '1rem',
        }}
      >
        <Button
          modal
          onClick={handleSelectHolder}
          disabled={!selectedBeneficiary}
          className="notiflix-confirm-button"
          style={{ backgroundColor: '#0a5f55', color: '#fff' }}
        >
          Confirmar
        </Button>
        <Button
          secondary
          modal
          onClick={Modal2.Close}
          style={{
            backgroundColor: 'transparent',
            border: '1px solid #676767',
          }}
        >
          Cancelar
        </Button>
      </div>
    </HolderContentStyled>
  );
};

export const useHandleOpenHolderModal = (
  getDataToTable: (filter: any) => void,
) => {
  const handleOpenSuccessModal = useCallback(() => {
    Modal2.Success({
      closable: false,
      autoWidth: true,
      title: 'Perfeito!',
      text: 'Troca realizada com sucesso!',
      children: (
        <Button
          modal
          style={{ backgroundColor: '#0a5f55', color: '#fff' }}
          onClick={() => {
            Modal2.Close();
          }}
        >
          Ok
        </Button>
      ),
    });
  }, []);

  const handleConfirm = useCallback(
    (callback: () => Promise<boolean>) => {
      Modal2.Warning({
        closable: false,
        autoWidth: true,
        title: 'Confirmação de troca de Titular!',
        text: 'O dependente selecionado será migrado para outro titular',
        children: (
          <ConfirmModalContent
            callback={callback}
            handleOpenSuccessModal={handleOpenSuccessModal}
          />
        ),
      });
    },
    [handleOpenSuccessModal],
  );

  const handleOpenHolderModal = (props: ToDependentModalProps): void => {
    Modal2.Generic({
      closable: false,
      children: (
        <HolderModalContent
          beneficiaryName={props.beneficiaryName}
          contractId={props.contractId}
          requestId={props.requestId}
          handleConfirm={handleConfirm}
          getDataToTable={getDataToTable}
        />
      ),
    });
  };

  return { handleOpenHolderModal };
};

const ToDependentModal: React.FC<ToDependentModalProps> = ({
  contractId,
  beneficiaryName,
  requestId,
}) => {
  const formRef = useRef<FormHandles>(null);
  const selectHandlerRef = useRef<SelectHandles>(null);
  const [beneficiaryTableData, setBeneficiaryTableData] = useState<
    Partial<BeneficiaryModalTable>[]
  >([]);
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [selectedBeneficiary, setSelectedBeneficiary] =
    useState<BeneficiaryModalTable | null>(null);
  const [statusOptions, setStatusOptions] = useState<OptionProps[]>([
    { title: 'Selecione...', value: '' },
  ]);
  const [statusSelected, setStatusSelected] = useState<string | undefined>();
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchProbableOwner = useCallback(
    (search?: Partial<SearchBeneficiaries>, page = 1, append = false) => {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsTableLoading(true);
        Notiflix.Block.circle('.notiflix-table-loading');
      }

      api
        .get('/beneficiary-dependent-holder/list-probable-holder', {
          params: {
            contractId,
            statusId: search?.statusId,
            cardId: search?.cardId,
            requestId: search?.requestId,
            page,
          },
        })
        .then((res) => {
          const newData = res.data?.data || [];

          if (append) {
            setBeneficiaryTableData((prev) => [...prev, ...newData]);
          } else {
            setBeneficiaryTableData(newData);
          }

          setHasMoreData(newData.length === 10);
          setCurrentPage(page);
        })
        .catch((error) => {
          console.error('Erro ao carregar dados:', error);
          Notiflix.Notify.failure('Erro ao carregar dados');
        })
        .finally(() => {
          if (append) {
            setIsLoadingMore(false);
          } else {
            setIsTableLoading(false);
            Notiflix.Block.remove('.notiflix-table-loading');
          }
        });
    },
    [contractId],
  );

  const fetchStatusSelectOptions = useCallback(() => {
    Notiflix.Block.circle('.notiflix-select-loading');

    api
      .get('/beneficiary-dependent-holder/list-status', {
        params: {
          contractId,
        },
      })
      .then((res) => {
        if (Array.isArray(res.data)) {
          return;
        }
        const options =
          res.data?.data?.status?.map(
            (s: Status) =>
              ({
                value: s.statusId,
                title: s.status,
              } as OptionProps),
          ) ?? [];
        setStatusOptions([{ title: 'Selecione...', value: '' }, ...options]);
      })
      .finally(() => {
        Notiflix.Block.remove('.notiflix-select-loading');
      });
  }, [contractId]);

  const handleSelectedBeneficiary = useCallback(
    (e: DataTableSelectionChangeParams) => setSelectedBeneficiary(e.value),
    [],
  );

  const handleSearchButtonClick = useCallback(
    (data: Partial<SearchBeneficiaries>) => {
      setCurrentPage(1);
      setHasMoreData(true);
      setSelectedBeneficiary(null);

      fetchProbableOwner(
        {
          statusId: statusSelected || '',
          cardId: data.cardId || '',
          requestId: data.requestId || '',
        },
        1,
        false,
      );
    },
    [fetchProbableOwner, statusSelected],
  );

  const handleStatusChange = useCallback(
    (value: string) => setStatusSelected(value),
    [],
  );

  const handleCleanSearchForm = useCallback(() => {
    selectHandlerRef.current?.setValue('Selecione...', '');
    formRef.current?.reset(searchFormInitialValues);
    setSelectedBeneficiary(null);
    setCurrentPage(1);
    setHasMoreData(true);
    setStatusSelected('');
    fetchProbableOwner(
      {
        statusId: '',
        cardId: '',
        requestId: '',
      },
      1,
      false,
    );
  }, [selectHandlerRef, formRef, fetchProbableOwner]);

  const handleCancelButtonClick = useCallback(() => {
    Modal2.Close();
  }, []);

  const handleConfirmButtonClick = useCallback(() => {
    Notiflix.Block.circle('.notiflix-confirm-button');
    api
      .post('/beneficiary-dependent-holder/to-dependent', {
        requestId,
        cardId: selectedBeneficiary?.cardId,
        holderId: selectedBeneficiary?.holderId,
      })
      .then(() => {
        Notiflix.Notify.success('Conversão realizado com sucesso!');
        Modal2.Close();

        // Navigate to dependente URL after successful conversion
        const newUrl = convertTitularToDependenteUrl(requestId);
        window.location.replace(newUrl);
      })
      .catch((err) => {
        Notiflix.Notify.failure(
          err.response?.data?.message ??
            'Falha ao converter titular a beneficiário',
        );
      })
      .finally(() => {
        Notiflix.Block.remove('.notiflix-confirm-button');
      });
  }, [requestId, selectedBeneficiary]);

  const handleLoadMore = useCallback(
    (rowsToShow: number) => {
      if (isLoadingMore || !hasMoreData) return;

      const currentSearch =
        formRef.current?.getData() as Partial<SearchBeneficiaries>;

      fetchProbableOwner(
        { ...currentSearch, statusId: statusSelected },
        currentPage + 1,
        true,
      );
    },
    [
      fetchProbableOwner,
      statusSelected,
      currentPage,
      hasMoreData,
      isLoadingMore,
    ],
  );

  useEffect(() => {
    fetchProbableOwner({}, 1, false);
    fetchStatusSelectOptions();
  }, [fetchStatusSelectOptions]);

  return (
    <HolderContentStyled>
      <h3>DEPENDENTE: {beneficiaryName}</h3>

      <ModalFormWrapper
        onSubmit={handleSearchButtonClick}
        ref={formRef}
        className="form-change-holder"
      >
        <div className="form-group-content">
          <div className="form-group">
            <Select
              name="statusId"
              title="Status:"
              ref={selectHandlerRef}
              className="notiflix-select-loading"
              options={statusOptions}
              changeCallback={handleStatusChange}
            />

            <Input
              name="cardId"
              label="Carteirinha:"
              placeholder="Número da carteirinha"
            />

            <Button
              type="submit"
              modal
              disabled={isTableLoading}
              style={{ backgroundColor: '#0a5f55', color: '#fff' }}
            >
              CONSULTAR
            </Button>
          </div>

          <div className="form-group">
            <Input
              name="holderName"
              label="Nome do Beneficiário:"
              placeholder="Nome do beneficiário"
            />

            <Button
              secondary
              disabled={isTableLoading}
              onClick={handleCleanSearchForm}
            >
              LIMPAR
            </Button>
          </div>
        </div>

        <div className="responsive-buttons">
          <Button
            type="submit"
            modal
            disabled={isTableLoading}
            style={{ backgroundColor: '#0a5f55', color: '#fff' }}
          >
            CONSULTAR
          </Button>
          <Button
            secondary
            disabled={isTableLoading}
            onClick={handleCleanSearchForm}
          >
            LIMPAR
          </Button>
        </div>

        <div className="table-container">
          <Table
            loading={isTableLoading}
            items={beneficiaryTableData}
            selection={selectedBeneficiary}
            onSelectionChange={handleSelectedBeneficiary}
            className="notiflix-table-loading"
            callbackShowMore={handleLoadMore}
            maxItems={hasMoreData ? 999999 : beneficiaryTableData.length}
          >
            <Column selectionMode="single" style={{ width: '25px' }} />
            <Column
              field="holderId"
              header="Solicitação"
              sortable
              body={(colData) => AddField(colData.holderId, 'Solicitação')}
              style={{ width: '140px' }}
            />
            <Column
              field="cardId"
              header="Carteirinha"
              sortable
              body={(colData) => AddField(colData.cardId, 'Carteirinha')}
              style={{ width: '140px' }}
            />
            <Column
              field="beneficiaryName"
              header="Titular"
              sortable
              body={(colData) => AddField(colData.beneficiaryName, 'Titular')}
              style={{ width: '140px' }}
            />
            <Column
              field="status"
              header="Situação"
              sortable
              body={(colData) =>
                possibleHolderStatusTag(colData.statusId, colData.status)
              }
              style={{ width: '140px' }}
            />
          </Table>
        </div>
      </ModalFormWrapper>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '1rem',
          gap: '1rem',
        }}
      >
        <Button
          secondary
          modal
          onClick={handleCancelButtonClick}
          style={{
            backgroundColor: 'transparent',
            border: '1px solid #676767',
          }}
        >
          CANCELAR
        </Button>
        <Button
          modal
          disabled={!selectedBeneficiary}
          onClick={handleConfirmButtonClick}
          className="notiflix-confirm-button"
          style={{ backgroundColor: '#0a5f55', color: '#fff' }}
        >
          CONFIRMAR
        </Button>
      </div>
    </HolderContentStyled>
  );
};

export default ToDependentModal;
